import { useProjectParams } from '@hooks/useProjectParams';
import { ChooseSectionText } from 'components/ChooseSectionText';
import { Toolbar } from 'components/Toolbar';
import {
    findAllConstructionTypesFromObjects,
    WorkOrderConstructionTypesFromObjects,
} from 'components/WorkOrderConstructionTypesTabSelect';
import { useRequest } from 'features/request/useRequest';
import type {
    BimActKc2VersionOtherItemData,
    BimChapterEnum,
    BimPriceLevelEnum,
    ConstructionTypeData,
    WorkOrderChangeObjectData,
} from 'gql/graphql';
import { SectionEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { usePageFilterSaver } from 'hooks/usePageFilterSaver';
import { usePreviousValue } from 'hooks/usePrevious';
import { useProjectData } from 'hooks/useProjectData';
import { BimPriceLevelChoice } from 'pages/Project/BimProject/BimCommonComponents/BimPriceLevelChoice';
import { PageHeaderLayoutContext } from 'pages/Project/Common/Layouts/PageLayoutContext';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';
import { SelectVersionsWorkOrder } from 'pages/Project/Common/WorkOrderVersionSelect';
import type { IListItemVersion } from 'types/Contract';
import { Button, cnMixSpace, IconBackward } from 'uikit';
import { isNumber } from 'utils';

import type { ElementRef } from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getWorkOrderVersionListItems } from '../../Common/utils';
import { BimWorkOrderFilterWithTools } from '../WorkOrder/BimWorkOrderFilter';
import { BimWorkOrderKebabMenu } from '../WorkOrder/BimWorkOrderKebabMenu';
import { BimWorkOrderPriceCounter } from '../WorkOrder/BimWorkOrderPriceCounter';
import type { BimWorkOrderPriceTable } from '../WorkOrder/BimWorkOrderPriceTable';
import { useBimWorkOrderTotalsRow, useGetBimWorkOrderDataFromProject } from '../WorkOrder/hooks';
import {
    BimWorkOrderRowTypeEnum,
    isBimChapter,
    isOtherBimChapter,
    isTotalsBimChapter,
    totalsByConstructionTypeKey,
} from '../WorkOrder/types';

import { BimWorkOrderOthersVersionTable } from './BimWorkOrderOthersVersionTable/BimWorkOrderOthersVersionTable';
import { useBimActsKC2VersionOther } from './BimWorkOrderOthersVersionTable/hooks/useBimActsKC2VersionOther';
import { BimActKc2VersionOtherRead } from './BimWorkOrderPriceVersionTable/BimActKc2VersionOtherRead/BimActKc2VersionOtherRead';
import { BimWorkOrderPriceVersionTable } from './BimWorkOrderPriceVersionTable/BimWorkOrderPriceVersionTable';
import { BimWorkOrderTotalsVersionTable } from './BimWorkOrderTotalsVersionTable/BimWorkOrderTotalsVersionTable';
import { BimWorkOrderVersionRequisites } from './bimWorkOrderVersionRequisites';
import { useBimWorkOrderPriceVersionCounter } from './hooks/useBimWorkOrderPriceVersionCounter';
import { useBimWorkOrderVersionObjects } from './hooks/useBimWorkOrderVersionObjects';
import { bimWorkOrderVersionsGql } from './workOrderVersionQueries';

import './WorkOrderVersion.css';

const TABLE_SETTINGS_HEIGHT = 32;
const PAGINATION_HEIGHT = 74;

export const BimWorkOrderVersion = () => {
    const { id: projectId, orderId, versionId, methodology } = useProjectParams();
    const { projectData, refreshProject } = useProjectData(projectId);
    const [constructionType, setConstructionType] = useState<ConstructionTypeData>();
    const [workOrderSectionOrChapter, setWorkOrderSectionOrChapter] = useState<IListItemVersion<string>>();
    const [workOrderRootObject, setWorkOrderRootObject] = useState<IListItemVersion<number>>();
    const [isOpenModalRead, setIsOpenModalRead] = useState(false);
    const [selectedActKc2Other, setSelectedActKc2Other] = useState<BimActKc2VersionOtherItemData>();
    const [position, setPosition] = useState<number | undefined>();
    const [priceLevel, setPriceLevel] = useState<BimPriceLevelEnum>();

    const { isHeaderOpened } = useContext(PageHeaderLayoutContext);

    const statusProject = projectData?.status;

    const contractStatus = projectData?.contractNavigation?.data?.contractNavData?.status;

    const { orderBim: orderRules } = useAccess();

    const priceTableApiRef = useRef<ElementRef<typeof BimWorkOrderPriceTable>>(null);

    const prevVersionId = usePreviousValue(versionId);

    const tableRef = useRef<HTMLDivElement>(null);
    const workOrderFilterRef = useRef<ElementRef<typeof BimWorkOrderFilterWithTools>>(null);
    const resetCheckedRowsRef = useRef<() => void>();

    const navigate = useNavigate();

    const workOrderId = Number(orderId);
    const workOrderVersionId = Number(versionId);

    const isConstructionTypesLoadedRef = useRef<boolean>(false);

    useEffect(() => {
        const isOrderIdChanged = versionId && prevVersionId && versionId !== prevVersionId;

        if (isOrderIdChanged) {
            isConstructionTypesLoadedRef.current = false;
        }
    }, [prevVersionId, versionId]);

    const variables = useMemo(
        () => ({
            workOrderId,
        }),
        [workOrderId],
    );

    const [bimWorkOrderVersionsData] = useRequest({
        requestId: 'bimWorkOrderVersions',
        query: bimWorkOrderVersionsGql,
        variables,
        withEffect: true,
    });

    const versionSelectItems = useMemo(
        () => getWorkOrderVersionListItems(bimWorkOrderVersionsData?.bimWorkOrderVersions),
        [bimWorkOrderVersionsData?.bimWorkOrderVersions],
    );

    const { reset: resetBimWorkOrderObjects, data: bimWorkOrderObjects } = useBimWorkOrderVersionObjects({
        requestKey: 'ForTypesAndObjectsVersion',
        workOrderVersionId,
    });

    const { savedFilterState, handleSaveFilterState } = usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: ({ savedValuesObj }) => {
                    const savedConstructionTypeId = savedValuesObj?.constructionTypeId;
                    const savedFilters = savedValuesObj?.[String(savedConstructionTypeId)];

                    const savedConstructionType = findAllConstructionTypesFromObjects({
                        workOrderChangeObjectsData:
                            bimWorkOrderObjects?.items as unknown as WorkOrderChangeObjectData[],
                    }).find((type) => type.id === savedConstructionTypeId);

                    if (savedConstructionType) {
                        setConstructionType(savedConstructionType);
                    }
                    if (savedFilters) {
                        setWorkOrderSectionOrChapter(savedFilters?.chapter ?? undefined);
                        setWorkOrderRootObject(savedFilters?.object ?? undefined);
                    }
                },
                withEmptyFilterSetter: bimWorkOrderObjects?.items
                    ? (setter: (params: { key: string | undefined; newState: any }) => void) => {
                          const constructionTypes = findAllConstructionTypesFromObjects({
                              workOrderChangeObjectsData:
                                  bimWorkOrderObjects?.items as unknown as WorkOrderChangeObjectData[],
                          });
                          const [firstConstructionType] = constructionTypes;

                          setConstructionType(firstConstructionType);
                          setter({
                              key: 'constructionTypeId',
                              newState: firstConstructionType?.id,
                          });
                      }
                    : undefined,
            }),
            [bimWorkOrderObjects?.items],
        ),
    );

    const handleConstructionTypeChange = useCallback(
        (newConstructionType: ConstructionTypeData) => {
            setConstructionType(newConstructionType);

            setTimeout(() => {
                setWorkOrderSectionOrChapter(savedFilterState?.[newConstructionType?.id]?.chapter ?? undefined);
                setWorkOrderRootObject(savedFilterState?.[newConstructionType?.id]?.object ?? undefined);
            }, 0);

            handleSaveFilterState({
                key: 'constructionTypeId',
                newState: newConstructionType.id,
            });
        },
        [handleSaveFilterState, savedFilterState],
    );

    const activeItemVersion = useMemo(
        () => versionSelectItems?.find(({ id }: { id: number }) => id === workOrderVersionId),
        [versionSelectItems, workOrderVersionId],
    );

    const breadCrumbsItems = useMemo(
        () => [
            {
                label: projectData?.name,
                href: `/projects/${methodology}/${projectId}`,
            },
            {
                label: 'Договор',
                href: `/projects/${methodology}/${projectData?.uniqueId}/contract`,
            },
        ],
        [projectData?.name, projectData?.uniqueId, methodology, projectId],
    );

    const handleBackVersion = useCallback(
        () => navigate(`/projects/${methodology}/${projectId}/order/${orderId}`),
        [methodology, navigate, orderId, projectId],
    );

    const bimChapter = useMemo(
        () =>
            isBimChapter(workOrderSectionOrChapter?.id) ? (workOrderSectionOrChapter?.id as BimChapterEnum) : undefined,
        [workOrderSectionOrChapter?.id],
    );
    const bimOtherChapter = useMemo(
        () =>
            isOtherBimChapter(workOrderSectionOrChapter?.id)
                ? (workOrderSectionOrChapter?.id as BimChapterEnum)
                : undefined,
        [workOrderSectionOrChapter?.id],
    );
    const bimTotalsChapter = useMemo(
        () => (isTotalsBimChapter(workOrderSectionOrChapter?.id) ? workOrderSectionOrChapter?.id : undefined),
        [workOrderSectionOrChapter?.id],
    );

    useEffect(
        () => () => {
            resetBimWorkOrderObjects(undefined);
        },
        [resetBimWorkOrderObjects],
    );

    const onRootObjectChange = useCallback(
        (rootObject: IListItemVersion<number> | undefined) => {
            setWorkOrderRootObject(rootObject);

            handleSaveFilterState({
                rootKey: String(constructionType?.id),
                key: 'object',
                newState: rootObject,
            });
        },
        [constructionType?.id, handleSaveFilterState],
    );

    const { makeRefreshDetailsData } = useBimWorkOrderTotalsRow({
        chapter: bimChapter,
        workOrderId,
        workOrderObjectId: workOrderRootObject?.workOrderVersionId,
        constructionTypeId: constructionType?.id,
        withEffect: false,
        priceLevel,
    });

    const onSectionOrChapterChange = useCallback(
        (sectionOrChapter: IListItemVersion<string>) => {
            setWorkOrderSectionOrChapter(sectionOrChapter);

            const isTotalsChapter =
                sectionOrChapter?.id === totalsByConstructionTypeKey ||
                sectionOrChapter?.id === SectionEnum.WorkOrderTotals;

            if (isTotalsChapter || !sectionOrChapter) {
                onRootObjectChange(undefined);
                handleSaveFilterState({
                    key: String(constructionType?.id),
                    newState: {
                        object: undefined,
                        chapter: sectionOrChapter,
                    },
                });
            } else {
                handleSaveFilterState({
                    rootKey: String(constructionType?.id),
                    key: 'chapter',
                    newState: sectionOrChapter,
                });
            }
        },
        [constructionType?.id, handleSaveFilterState, onRootObjectChange],
    );

    const {
        workOrderTitle,
        workOrderStatus,
        hasInProgress,
        hasActsKC2,
        hasWorkOrderChangesValidStatus,
        isWorkOrderChangeRequired,
        isFrameContract,
    } = useGetBimWorkOrderDataFromProject({});

    const isShowPriceTablePossible = !!((workOrderRootObject && (bimChapter || bimOtherChapter)) || bimTotalsChapter);

    const { priceListVersionCounter, chaptersTooltip, makeRequestPriceListData } = useBimWorkOrderPriceVersionCounter({
        workOrderVersionId,
        workOrderSectionOrChapter,
        constructionTypeId: constructionType?.id,
        workOrderVersionRootObjectId: workOrderRootObject?.id,
    });

    const toggleTotalChapterDetailsOuter = useCallback(() => {
        setTotalChapterDetailsRowOpenOuter((prev) =>
            prev
                ? null
                : {
                      data: null,
                      rowType: BimWorkOrderRowTypeEnum.TotalsString,
                  },
        );
    }, []);

    const [totalChapterDetailsRowOpenOuter, setTotalChapterDetailsRowOpenOuter] = useState<{
        rowType: BimWorkOrderRowTypeEnum.TotalsString;
        data: null;
    } | null>(null);

    const makeRefreshCurrentTable = useCallback(() => {
        if (priceTableApiRef?.current) {
            priceTableApiRef?.current?.api?.refreshInfiniteCache();

            if (!bimOtherChapter) {
                makeRefreshDetailsData();
                makeRequestPriceListData();
            }
        }
    }, [bimOtherChapter, makeRefreshDetailsData, makeRequestPriceListData]);

    useEffect(() => {
        if (workOrderFilterRef.current) {
            setPosition(workOrderFilterRef.current?.getBoundingClientRect().bottom);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workOrderFilterRef, isHeaderOpened, workOrderFilterRef.current?.getBoundingClientRect().bottom]);

    const onOpenActKc2Read = useCallback((act: any) => {
        setSelectedActKc2Other(act);
        setIsOpenModalRead(true);
    }, []);

    const onClose = useCallback(() => {
        setSelectedActKc2Other(undefined);
        setIsOpenModalRead(false);
    }, []);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isBimPresenceActs } = useBimActsKC2VersionOther(
        workOrderVersionId,
        workOrderRootObject?.id,
        !!bimOtherChapter,
    );

    const handleReadStructureVersion = useCallback(
        () => navigate(`/projects/${methodology}/${projectId}/order/${orderId}/version/${versionId}/structure`),
        [methodology, navigate, orderId, projectId, versionId],
    );

    return (
        <ProjectPageLayout
            layoutEntityKey={workOrderId}
            title={workOrderTitle}
            statusWorkOrder={workOrderStatus}
            breadCrumbsItems={breadCrumbsItems}
            isWorkOrderChangeRequired={isWorkOrderChangeRequired}
            isHeaderCollapsable
            isHasBadge={false}
            headerBottom={
                <Toolbar flex>
                    <Button
                        className={cnMixSpace({ mR: 'xs' })}
                        label="Вернуться к текущей версии"
                        view="secondary"
                        size="s"
                        onClick={handleBackVersion}
                        iconLeft={IconBackward}
                    />
                    <Button
                        className={cnMixSpace({ mR: 'xs' })}
                        label="Просмотр структуры"
                        view="secondary"
                        size="s"
                        onClick={handleReadStructureVersion}
                    />
                    <BimWorkOrderVersionRequisites workOrderVersionId={workOrderVersionId} />
                    {!!bimWorkOrderVersionsData?.bimWorkOrderVersions.length && (
                        <SelectVersionsWorkOrder
                            data={bimWorkOrderVersionsData?.bimWorkOrderVersions}
                            activeItemVersion={activeItemVersion}
                        />
                    )}
                    <BimWorkOrderKebabMenu
                        statusWorkOrder={workOrderStatus}
                        statusProject={statusProject}
                        statusContract={contractStatus}
                        hasActsKC2={hasActsKC2}
                        hasWorkOrderChangesValidStatus={hasWorkOrderChangesValidStatus}
                        isFrameContract={isFrameContract}
                        orderRules={orderRules}
                        hasInProgress={hasInProgress}
                        projectData={projectData}
                        refreshProjectData={refreshProject}
                        isVersionPage={!!versionId}
                    />
                    {!bimOtherChapter && <BimPriceLevelChoice priceLevel={priceLevel} setPriceLevel={setPriceLevel} />}
                </Toolbar>
            }
        >
            <div
                id="workOrder"
                style={
                    {
                        '--table-settings-height': `${TABLE_SETTINGS_HEIGHT}px`,
                        '--others-height':
                            isNumber(tableRef.current?.getBoundingClientRect?.()?.top) &&
                            `${
                                (tableRef.current?.getBoundingClientRect?.()?.top as number) +
                                TABLE_SETTINGS_HEIGHT +
                                PAGINATION_HEIGHT
                            }px`,
                    } as React.CSSProperties
                }
            >
                <WorkOrderConstructionTypesFromObjects
                    onChange={handleConstructionTypeChange}
                    workOrderChangeObjectsData={bimWorkOrderObjects?.items ?? []}
                    activeConstructionTypeId={constructionType?.id}
                />
                <BimWorkOrderFilterWithTools
                    ref={workOrderFilterRef}
                    onRootObjectChange={onRootObjectChange}
                    onSectionOrChapterChange={onSectionOrChapterChange}
                    sectionOrChapter={workOrderSectionOrChapter}
                    rootObject={workOrderRootObject}
                    objectsListItems={bimWorkOrderObjects?.items}
                    constructionTypeId={constructionType?.id}
                    isFrameContract={isFrameContract}
                >
                    <div className="wrapperEstimate">
                        <BimWorkOrderPriceCounter
                            priceListCounter={priceListVersionCounter}
                            chaptersTooltip={chaptersTooltip}
                        />
                    </div>
                    {isOpenModalRead && !!bimOtherChapter && (
                        <BimActKc2VersionOtherRead
                            actKC2Other={selectedActKc2Other}
                            onClose={onClose}
                            workOrderId={workOrderId}
                            constructionType={constructionType}
                            selectedObject={workOrderRootObject}
                            priceTableApiRef={priceTableApiRef}
                        />
                    )}
                </BimWorkOrderFilterWithTools>
                {isShowPriceTablePossible ? (
                    <div
                        id="bimWorkOrderContainer"
                        style={
                            {
                                '--tableStartPosition': isNumber(position) && `${position ?? 0}px`,
                            } as React.CSSProperties
                        }
                    >
                        {bimChapter && (
                            <BimWorkOrderPriceVersionTable
                                priceLevel={priceLevel}
                                workOrderId={workOrderId}
                                workOrderVersionId={workOrderVersionId}
                                constructionTypeId={constructionType?.id}
                                workOrderRootObjectId={workOrderRootObject?.id}
                                chapter={bimChapter}
                                totalChapterDetailsRowOpenOuter={totalChapterDetailsRowOpenOuter}
                                toggleTotalChapterDetailsOuter={toggleTotalChapterDetailsOuter}
                                makeRefreshTable={makeRefreshCurrentTable}
                                ref={priceTableApiRef}
                                workOrderStatus={workOrderStatus}
                                resetCheckedRowsRef={resetCheckedRowsRef}
                                contractStatus={contractStatus}
                                statusProject={statusProject}
                                orderRules={orderRules}
                                hasInProgress={hasInProgress}
                                isFrameContract={isFrameContract}
                                isVersionPage={!!versionId}
                            />
                        )}
                        {bimTotalsChapter && (
                            <BimWorkOrderTotalsVersionTable
                                workOrderVersionId={workOrderVersionId}
                                constructionTypeId={constructionType?.id}
                                bimTotalsChapter={bimTotalsChapter}
                                workOrderStatus={workOrderStatus}
                                ref={priceTableApiRef}
                                hasInProgress={hasInProgress}
                                isFrameContract={isFrameContract}
                                priceLevel={priceLevel}
                            />
                        )}

                        {bimOtherChapter && (
                            <BimWorkOrderOthersVersionTable
                                workOrderId={workOrderId}
                                statusWorkOrder={workOrderStatus}
                                hasInProgress={hasInProgress}
                                constructionTypeId={constructionType?.id}
                                workOrderRootObjectId={workOrderRootObject?.id}
                                totalDetailsRowOpenOuter={!!totalChapterDetailsRowOpenOuter}
                                toggleTotalDetailsOuter={toggleTotalChapterDetailsOuter}
                                ref={priceTableApiRef}
                                isFrameContract={isFrameContract}
                                onOpenActKc2Read={onOpenActKc2Read}
                                refreshProject={refreshProject}
                                resetCheckedRowsRef={resetCheckedRowsRef}
                                contractStatus={contractStatus}
                                orderRules={orderRules}
                                statusProject={statusProject}
                                workOrderVersionId={workOrderVersionId}
                                isVersionPage={!!versionId}
                            />
                        )}
                    </div>
                ) : (
                    <ChooseSectionText
                        className="choiceMessage"
                        text="Для отображения данных выберите раздел и объект"
                    />
                )}
            </div>
        </ProjectPageLayout>
    );
};

BimWorkOrderVersion.displayName = 'BimWorkOrderVersion';
